import React from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ServicePageLayout } from '../components/ServicePageLayout';
export function MobileDetailingPage() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Mobile Auto Detailing',
    serviceType: 'Mobile Car Detailing',
    provider: {
      '@type': 'LocalBusiness',
      name: "Mike's Pro Detail",
      telephone: '+18183819905'
    },
    areaServed: ['Ventura County', 'Camarillo', 'Oxnard', 'Ventura', 'Thousand Oaks', 'Simi Valley', 'Moorpark', 'Newbury Park', 'Westlake Village'],
    description: 'Fully mobile car detailing throughout Ventura County. We bring the detail shop to your home or office with our own water and power.'
  };
  const faqs = [{
    question: 'What is mobile car detailing?',
    answer: "Mobile car detailing is a professional cleaning and restoration service that comes to you. Mike's Pro Detail brings all equipment, water, power, and premium products to your home or office anywhere in Ventura County — no drop-off or waiting room required."
  }, {
    question: 'Do you bring your own water and electricity?',
    answer: 'Yes. We are fully self-contained with our own water supply and generator. All we need is access to your vehicle and a reasonable amount of space to work.'
  }, {
    question: 'What areas do you cover for mobile detailing?',
    answer: 'We serve all of Ventura County including Camarillo, Oxnard, Ventura, Thousand Oaks, Simi Valley, Moorpark, Newbury Park, and Westlake Village, plus parts of the San Fernando Valley.'
  }, {
    question: 'How much does mobile detailing cost?',
    answer: 'Exterior details start at $160, interior at $180, and our popular full detail at $300, with adjustments for vehicle size. Call (818) 381-9905 for an exact quote.'
  }, {
    question: 'How long does mobile detailing take?',
    answer: 'A wash and wax takes about 1.5\u20132 hours, interior detailing 2\u20133 hours, and a full detail 3\u20134 hours. We never rush — quality results take proper time.'
  }];
  const breadcrumbs = [{
    name: 'Home',
    item: 'https://www.fulldetailpro.com/'
  }, {
    name: 'Services',
    item: 'https://www.fulldetailpro.com/#services'
  }, {
    name: 'Mobile Detailing',
    item: 'https://www.fulldetailpro.com/services/mobile-detailing'
  }];
  return <ServicePageLayout title="Mobile Detailing Ventura County" subtitle="The detail shop that comes to you. Professional mobile car detailing at your home or office anywhere in Ventura County — no drop-off, no waiting." metaDescription="Mobile detailing in Ventura County. Mike's Pro Detail brings interior, exterior & full car detailing to your home or office. 5-star service. Call (818) 381-9905." schema={schema} faqs={faqs} breadcrumbs={breadcrumbs} data-id="element-748">
      <p data-id="element-749">
        At Mike's Pro Detail,{' '}
        <strong data-id="element-750">mobile detailing in Ventura County</strong> means professional,
        showroom-quality car care delivered directly to your driveway. There's
        no need to drop your vehicle off, sit in a waiting room, or rearrange
        your day around a detail shop's hours. We bring a fully equipped
        detailing operation — complete with our own water, power, and premium
        products — straight to your home or office, wherever you are in Ventura
        County.
      </p>

      <h2 data-id="element-751">Why Choose Mobile Car Detailing?</h2>
      <p data-id="element-752">
        Mobile car detailing in Ventura County combines the highest quality
        results with unmatched convenience. Instead of automated car washes that
        scratch and swirl your paint, our technicians hand-detail every vehicle
        with meticulous care. And because we come to you, you reclaim hours of
        your day while we transform your car on-site.
      </p>
      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-753">
        <li className="flex items-start" data-id="element-754">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-755" />
          <span data-id="element-756">
            <strong data-id="element-757">Total Convenience:</strong> We detail your car at your home,
            office, or apartment — you never have to leave.
          </span>
        </li>
        <li className="flex items-start" data-id="element-758">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-759" />
          <span data-id="element-760">
            <strong data-id="element-761">Fully Self-Contained:</strong> We arrive with our own water
            and power, so no hookups are needed on your end.
          </span>
        </li>
        <li className="flex items-start" data-id="element-762">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-763" />
          <span data-id="element-764">
            <strong data-id="element-765">Premium, Hand-Crafted Results:</strong> Safe wash methods
            and professional-grade products on every vehicle.
          </span>
        </li>
        <li className="flex items-start" data-id="element-766">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-767" />
          <span data-id="element-768">
            <strong data-id="element-769">Flexible Scheduling:</strong> Including weekends, to fit
            your busy Ventura County lifestyle.
          </span>
        </li>
      </ul>

      <h2 data-id="element-770">Our Mobile Detailing Services</h2>
      <p data-id="element-771">
        Every service we offer is performed on-site as part of our mobile
        detailing operation. Explore each in detail:
      </p>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-772">
        <Link to="/services/interior-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-773">
          Interior Detailing &rarr;
        </Link>
        <Link to="/services/exterior-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-774">
          Exterior Detailing &rarr;
        </Link>
        <Link to="/services/full-car-detail" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-775">
          Full Car Detail &rarr;
        </Link>
        <Link to="/services/engine-bay-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-776">
          Engine Bay Detailing &rarr;
        </Link>
        <Link to="/services/headlight-restoration" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-777">
          Headlight Restoration &rarr;
        </Link>
        <Link to="/services/ceramic-coating" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-778">
          Ceramic Coating &rarr;
        </Link>
      </div>

      <h2 data-id="element-779">Serving All of Ventura County</h2>
      <p data-id="element-780">
        Our mobile detailing service reaches every community in Ventura County.
        Find your city for local details and pricing:{' '}
        <Link to="/ventura-county/camarillo" data-id="element-781">Camarillo</Link>,{' '}
        <Link to="/ventura-county/oxnard" data-id="element-782">Oxnard</Link>,{' '}
        <Link to="/ventura-county/ventura" data-id="element-783">Ventura</Link>,{' '}
        <Link to="/ventura-county/thousand-oaks" data-id="element-784">Thousand Oaks</Link>,{' '}
        <Link to="/ventura-county/simi-valley" data-id="element-785">Simi Valley</Link>,{' '}
        <Link to="/ventura-county/moorpark" data-id="element-786">Moorpark</Link>,{' '}
        <Link to="/ventura-county/newbury-park" data-id="element-787">Newbury Park</Link>, and{' '}
        <Link to="/ventura-county/westlake-village" data-id="element-788">Westlake Village</Link>.
      </p>
    </ServicePageLayout>;
}